import { setContext } from '@apollo/client/link/context'

import { type LanguageKey, getLocale } from '@/domain/locale'
import { OptimizelyClient } from '@/lib/optimizely/OptimizelyClient'
import OptimizelyServerManager from '@/lib/optimizely/OptimizelyServerManager'
import { serializeCookies } from '@/utils/cookies/serializeCookies'
import { isServer } from '@/utils/isSSR'

import { type RequestHeaders } from '../apolloClient'

const contextLink = (languageKey: LanguageKey, requestHeaders?: RequestHeaders) => {
  let localeLanguageKey: LanguageKey

  /**
   * If we're configuring this on the browser we should use the cookies value
   */
  if (typeof window !== 'undefined') {
    const { languageKey: cookieLanguageKey } = getLocale()
    localeLanguageKey = cookieLanguageKey
  }

  return setContext((_, previousContext) => {
    const { headers = {}, cookies } = requestHeaders || {}

    const newContext = {
      headers: {
        ...headers,
        ...(previousContext.headers || {}),
        'Accept-Language': localeLanguageKey || languageKey,
        ...(cookies ? { Cookie: serializeCookies(cookies) } : {}),
      },
    }

    if (previousContext.experiments) {
      const optimizely = isServer() ? OptimizelyServerManager : OptimizelyClient
      const optimizelyClient = optimizely.getClient()

      if (!optimizelyClient) {
        // TODO: Log something?
        return newContext
      }

      const flags =
        (Array.isArray(previousContext.experiments) && previousContext.experiments) || undefined

      return optimizelyClient.onReady().then(
        async (): Promise<{
          headers: Record<string, string | string[]>
        }> => {
          const optimizelyInstance = optimizely.getInstance()

          const experimentHeader =
            optimizelyInstance instanceof OptimizelyServerManager
              ? optimizelyInstance.decideTestGroupWithUserContext(
                  { id: requestHeaders?.cookies?.['machId'] },
                  flags
                )
              : await optimizelyInstance.getSessionTestGroups(flags)

          const { headers: newHeaders, ...restContext } = newContext

          return {
            ...restContext,
            headers: {
              ...newHeaders,
              ...(experimentHeader ? { ['x-experiments']: experimentHeader } : {}),
            },
          }
        }
      )
    }

    return newContext
  })
}

export default contextLink
